import React from 'react';

const OrderDetails = ({ orderData }) => {
  const {
    orderInfo = {},
    deliveryAddress = {},
    items = [],
    orderSummary = {},
    actions = []
  } = orderData || {};

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'returned':
        return 'text-fuchsia-500';
      case 'delivered':
        return 'text-green-500';
      case 'pending':
        return 'text-yellow-500';
      case 'cancelled':
        return 'text-red-500';
      default:
        return 'text-sky-950';
    }
  };

  const getItemStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'returned':
        return 'text-sky-950 opacity-70';
      case 'delivered':
        return 'text-green-500';
      default:
        return 'text-sky-950 opacity-70';
    }
  };

  return (
    <main className="flex flex-col grow items-center max-md:mt-9 max-md:max-w-full">
      {/* Order Information Section */}
      <section className="max-w-full rounded-none w-[804px]">
        <div className="flex flex-col px-5 py-4 w-full bg-white rounded-2xl shadow-2xl max-md:max-w-full">
          <h2 className="self-start text-lg font-semibold leading-none text-sky-950">
            Order Information
          </h2>
          <div className="shrink-0 mt-2.5 h-px border border-solid border-neutral-300 max-md:max-w-full" />
          <div className="flex flex-wrap gap-1.5 justify-between items-start mt-3 w-full text-base max-md:max-w-full">
            <div className="flex gap-10 rounded-none min-w-60 text-sky-950 w-[307px]">
              <span className="opacity-50 text-sky-950">Order ID</span>
              <span className="font-medium text-right text-sky-950">
                {orderInfo.orderId || '#ORD57986418741'}
              </span>
            </div>
            <div className="flex gap-10 rounded-none min-w-60 text-sky-950 w-[307px]">
              <span className="opacity-50 text-sky-950">Ordered At</span>
              <span className="font-medium text-right basis-auto text-sky-950">
                {orderInfo.orderedAt || '26 May, 2023 09:30 PM'}
              </span>
            </div>
            <div className="flex gap-10 whitespace-nowrap rounded-none min-w-60 w-[307px]">
              <span className="opacity-50 text-sky-950">Status</span>
              <span className={`font-medium text-right ${getStatusColor(orderInfo.status)}`}>
                {orderInfo.status || 'Returned'}
              </span>
            </div>
            <div className="flex gap-10 whitespace-nowrap rounded-none min-w-60 text-sky-950 w-[307px]">
              <span className="opacity-50 text-sky-950">Payment</span>
              <span className="font-medium text-right text-sky-950">
                {orderInfo.payment || 'Card'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Delivery Address Section */}
      <section className="mt-4 max-w-full text-base font-semibold rounded-none text-sky-950 w-[804px]">
        <div className="flex flex-col items-start px-5 py-4 w-full bg-white rounded-2xl shadow-2xl max-md:max-w-full">
          <h2 className="text-lg leading-none text-sky-950">
            Delivery address
          </h2>
          <div className="shrink-0 self-stretch mt-2.5 w-full h-px border border-solid border-neutral-300" />
          <div className="flex gap-2.5 items-center mt-2">
            <span className="self-stretch my-auto text-sky-950">
              {deliveryAddress.name || 'Jaane Cooper'}
            </span>
          </div>
          <address className="mt-1.5 tracking-normal text-black opacity-60 max-md:max-w-full not-italic">
            {deliveryAddress.address || '4517 Washington Ave. Manchester, Kentucky Preston Rd. Inglewood, 987456'}
          </address>
          <div className="mt-1.5 text-sm tracking-normal text-black opacity-60">
            {deliveryAddress.phone || '+1 888 3685 368'}
          </div>
        </div>
      </section>

      {/* Items Section */}
      <section className="mt-4 max-w-full font-medium rounded-none w-[804px]">
        <div className="flex flex-col items-start px-5 py-4 bg-white rounded-2xl shadow-2xl max-md:max-w-full">
          <h2 className="text-lg font-semibold leading-none text-sky-950">
            Items
          </h2>
          <div className="shrink-0 self-stretch mt-2.5 w-full h-px border border-solid border-neutral-300" />
          <div className="flex flex-col items-start mt-2.5 max-w-full w-[732px] gap-3.5">
            {items.length > 0 ? items.map((item, index) => (
              <div key={index} className="flex gap-3 max-w-full rounded-none w-[356px]">
                <img
                  src={item.image || 'https://cdn.acsdev.in/Rentastic/2.png'}
                  alt={item.name || 'Product image'}
                  className="object-contain shrink-0 self-start rounded-md aspect-square w-[46px]"
                />
                <div className="flex flex-col grow shrink-0 basis-0 w-fit">
                  <div className="text-base text-sky-950">
                    {item.quantity || 1} X {item.name || 'Loveable for someone Teddy Bear'}
                  </div>
                  <div className="flex gap-2.5 justify-center items-center self-start text-sm whitespace-nowrap text-sky-950">
                    <span className={`self-stretch my-auto ${getItemStatusColor(item.status)}`}>
                      {item.status || 'Returned'}
                    </span>
                  </div>
                </div>
              </div>
            )) : (
              // Default items if none provided
              <>
                <div className="flex gap-3 max-w-full rounded-none w-[356px]">
                  <img
                    src="https://cdn.acsdev.in/Rentastic/2.png"
                    alt="Teddy Bear"
                    className="object-contain shrink-0 self-start rounded-md aspect-square w-[46px]"
                  />
                  <div className="flex flex-col grow shrink-0 basis-0 w-fit">
                    <div className="text-base text-sky-950">
                      1 X Loveable for someone Teddy Bear
                    </div>
                    <div className="flex gap-2.5 justify-center items-center self-start text-sm whitespace-nowrap text-sky-950">
                      <span className="self-stretch my-auto opacity-70 text-sky-950">
                        Returned
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 max-w-full rounded-none w-[356px]">
                  <img
                    src="https://cdn.acsdev.in/Rentastic/3.png"
                    alt="Teddy Bear"
                    className="object-contain shrink-0 self-start rounded-md aspect-square w-[46px]"
                  />
                  <div className="flex flex-col grow shrink-0 basis-0 w-fit">
                    <div className="text-base text-sky-950">
                      1 X Loveable for someone Teddy Bear
                    </div>
                    <div className="flex gap-2.5 justify-center items-center self-start text-sm whitespace-nowrap text-sky-950">
                      <span className="self-stretch my-auto opacity-70 text-sky-950">
                        Returned
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Order Summary Section */}
      <section className="mt-4 max-w-full text-lg rounded-2xl w-[804px]">
        <div className="flex flex-col px-5 py-4 w-full bg-white rounded-2xl shadow-2xl max-md:max-w-full">
          <h2 className="self-start font-semibold leading-none text-sky-950">
            Order Summary
          </h2>
          <div className="shrink-0 mt-2.5 max-w-full h-px border border-solid border-neutral-300 w-[764px]" />
          <div className="flex flex-wrap gap-5 justify-between mt-4 text-base tracking-normal max-md:max-w-full">
            <div className="flex flex-col text-sky-950">
              <div className="opacity-90 text-sky-950 max-md:mr-2.5">
                {orderSummary.premiumChargeLabel || 'Premium toys charge'}
              </div>
              <div className="self-start mt-3 opacity-90 text-sky-950">
                {orderSummary.shippingChargeLabel || 'Shipping charge'}
              </div>
              <div className="mt-3 opacity-90 text-sky-950">
                {orderSummary.discountLabel || 'Discount'}{' '}
                <span style={{fontWeight: 600, fontSize: '14px'}}>
                  {orderSummary.discountSubLabel || '(Premium toys)'}
                </span>
              </div>
            </div>
            <div className="font-medium text-right whitespace-nowrap">
              <div className="flex flex-col pl-1.5">
                <div className="text-sky-950">
                  {orderSummary.premiumCharge || '$30.00'}
                </div>
                <div className="self-start mt-3 text-sky-950">
                  {orderSummary.shippingCharge || '$10.00'}
                </div>
              </div>
              <div className="mt-3 text-red-500">
                {orderSummary.discount || '-$10.00'}
              </div>
            </div>
          </div>
          <div className="shrink-0 mt-4 max-w-full h-px border border-solid border-neutral-200 w-[764px]" />
          <div className="flex flex-wrap gap-5 justify-between mt-5 font-semibold tracking-normal whitespace-nowrap text-sky-950 max-md:max-w-full">
            <span className="text-sky-950">Total</span>
            <span className="text-right text-sky-950">
              {orderSummary.total || '$20.00'}
            </span>
          </div>
        </div>
      </section>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-5 justify-center items-center mt-5 max-w-full text-base font-semibold tracking-wide leading-tight text-center w-[761px] max-md:mr-2.5">
        {actions.length > 0 ? actions.map((action, index) => (
          <button
            key={index}
            className={`flex gap-10 justify-center items-center px-5 py-4 rounded-2xl border-b border-white border-opacity-30 min-h-[50px] ${
              action.primary
                ? 'text-white bg-green-500 shadow-lg border-b-white border-b-opacity-30'
                : 'text-neutral-600 bg-zinc-300'
            }`}
            onClick={action.onClick}
            disabled={action.disabled}
            aria-label={action.ariaLabel || action.label}
          >
            <span className={action.primary ? 'text-white' : 'text-neutral-600'}>
              {action.label}
            </span>
          </button>
        )) : (
          // Default buttons if none provided
          <>
            <button className="flex gap-10 justify-center items-center px-5 py-4 rounded-2xl border-b bg-zinc-300 border-white border-opacity-30 min-h-[50px] text-neutral-600">
              <span>Rate & Review</span>
            </button>
            {/* <button className="flex gap-10 justify-center items-center px-5 py-4 rounded-2xl border-b bg-zinc-300 border-white border-opacity-30 min-h-[50px] text-neutral-600">
              <span>Repeat Order</span>
            </button> */}
            {/* <button className="flex gap-10 justify-center items-center px-5 py-4 text-white bg-green-500 rounded-2xl border-b border-solid shadow-lg border-b-white border-b-opacity-30 min-h-[50px]">
              <span className="text-white">Purchase Permanently</span>
            </button> */}
          </>
        )}
      </div>
    </main>
  );
};

export default OrderDetails;
